import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Placeholder } from '@tiptap/extensions';
import { useI18n } from '../contexts/I18nContext';
import './RichTextEditor.css';

// Normalize TipTap's empty document ("<p></p>") to an empty string so callers
// can treat "no content" uniformly (change detection, conditional rendering).
const normalize = (html) => (html === '<p></p>' ? '' : html);

// Shared rich-text editor built on TipTap (tiptap.dev) with the StarterKit —
// all of TipTap's default extensions/plugins (bold, italic, underline, strike,
// headings, lists, blockquote, code, code block, horizontal rule, link, history,
// …). Edits HTML; `value`/`onChange` make it a controlled component.
export default function RichTextEditor({ value, onChange, placeholder }) {
  const { t } = useI18n();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: {
          openOnClick: false,
          autolink: true,
          HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
        },
      }),
      Placeholder.configure({ placeholder: placeholder || '' }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(normalize(editor.getHTML())),
    editorProps: { attributes: { class: 'rich-content' } },
  });

  // Sync external value changes (e.g. switching which item is being edited)
  // without clobbering the caret while the user is typing.
  useEffect(() => {
    if (!editor) return;
    const incoming = value || '';
    if (incoming !== normalize(editor.getHTML())) {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt(t('rich.link'), prev || 'https://');
    if (url === null) return; // cancelled
    const chain = editor.chain().focus().extendMarkRange('link');
    if (url.trim() === '') chain.unsetLink().run();
    else chain.setLink({ href: url.trim() }).run();
  };

  const Btn = ({ onClick, active, title, children }) => (
    <button
      type="button"
      title={title}
      className={active ? 'active' : ''}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    >
      {children}
    </button>
  );

  return (
    <div className="rich-editor">
      <div className="rich-toolbar">
        <Btn title={t('rich.bold')} active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><b>B</b></Btn>
        <Btn title={t('rich.italic')} active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><i>I</i></Btn>
        <Btn title={t('rich.underline')} active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><u>U</u></Btn>
        <Btn title={t('rich.strike')} active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}><s>S</s></Btn>
        <span className="rich-sep" />
        <Btn title={t('rich.heading')} active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H</Btn>
        <Btn title={t('rich.list')} active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>•</Btn>
        <Btn title={t('rich.ordered_list')} active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>1.</Btn>
        <Btn title={t('rich.quote')} active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>❝</Btn>
        <Btn title={t('rich.code')} active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()}>{'</>'}</Btn>
        <span className="rich-sep" />
        <Btn title={t('rich.link')} active={editor.isActive('link')} onClick={setLink}>🔗</Btn>
        <Btn title={t('rich.clear_format')} onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>✕</Btn>
        <span className="rich-sep" />
        <Btn title={t('rich.undo')} onClick={() => editor.chain().focus().undo().run()}>↶</Btn>
        <Btn title={t('rich.redo')} onClick={() => editor.chain().focus().redo().run()}>↷</Btn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
